/* aestheticwolf — PDP v2 helpers
   1. Smooth accordions (.pdp-v2-acc, info column + FAQ)
   2. Keeps v2-only elements (stock line, price in the add to cart) in sync on variant change
   3. Floating add to cart on mobile
   4. Colour dots in the set card
   Relies on the theme's pubsub.js / global.js (subscribe, PUB_SUB_EVENTS) when present. */
(() => {
  if (window.pdpV2Loaded) return;
  window.pdpV2Loaded = true;

  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  /* 1. Accordions ------------------------------------------------------- */
  const afterTransition = (el, cb) => {
    let done = false;
    const finish = (event) => {
      if (event && event.target !== el) return;
      if (done) return;
      done = true;
      el.removeEventListener('transitionend', finish);
      cb();
    };
    el.addEventListener('transitionend', finish);
    window.setTimeout(finish, 450);
  };

  document.addEventListener('click', (event) => {
    const summary = event.target.closest('.pdp-v2-acc > .pdp-v2-acc__summary');
    if (!summary) return;
    const details = summary.parentElement;
    const body = details.querySelector(':scope > .pdp-v2-acc__body');
    if (!body || reduceMotion.matches) return;

    event.preventDefault();
    if (details.dataset.animating === 'true') return;
    details.dataset.animating = 'true';

    const cleanUp = () => {
      body.style.transition = '';
      body.style.height = '';
      details.classList.remove('is-opening', 'is-closing');
      delete details.dataset.animating;
    };

    if (details.open) {
      body.style.height = `${body.scrollHeight}px`;
      details.classList.add('is-closing');
      window.requestAnimationFrame(() => {
        body.style.transition = 'height 0.3s ease';
        body.style.height = '0px';
      });
      afterTransition(body, () => {
        details.open = false;
        cleanUp();
      });
    } else {
      details.open = true;
      details.classList.add('is-opening');
      const target = body.scrollHeight;
      body.style.height = '0px';
      window.requestAnimationFrame(() => {
        body.style.transition = 'height 0.3s ease';
        body.style.height = `${target}px`;
      });
      afterTransition(body, cleanUp);
    }
  });

  /* 2. Variant sync ----------------------------------------------------- */
  const syncFromHtml = (html) => {
    if (!html) return;
    document.querySelectorAll('.pdp-v2 [data-pdp-v2-sync][id]').forEach((el) => {
      const source = html.getElementById(el.id);
      if (source) el.innerHTML = source.innerHTML;
    });
  };

  const initVariantSync = () => {
    if (typeof window.subscribe !== 'function' && typeof subscribe !== 'function') return false;
    if (typeof PUB_SUB_EVENTS === 'undefined') return false;
    subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
      const data = event && event.data;
      if (data && data.html) syncFromHtml(data.html);
    });
    return true;
  };

  /* 3. Floating add to cart (mobile) ------------------------------------ */
  const initStickyAtc = (root) => {
    const bar = root.querySelector('[data-pdp-v2-sticky-atc]');
    const main = root.querySelector('[data-pdp-v2-atc]');
    if (!bar || !main || bar.dataset.ready === 'true') return;
    bar.dataset.ready = 'true';

    const button = bar.querySelector('[data-pdp-v2-sticky-btn]');
    const label = bar.querySelector('[data-pdp-v2-sticky-label]');
    const price = bar.querySelector('[data-pdp-v2-sticky-price]');
    const anchor = main.closest('.pdp-v2-form__buttons') || main;
    const footer = document.querySelector('footer');
    const mobile = window.matchMedia('(max-width: 749px)');

    let pastButton = false;
    let footerInView = false;

    const render = () => {
      const show = mobile.matches && pastButton && !footerInView;
      bar.classList.toggle('is-visible', show);
      bar.setAttribute('aria-hidden', show ? 'false' : 'true');
      button.tabIndex = show ? 0 : -1;
    };

    const notify = root.querySelector('.klaviyo-bis-trigger');
    const notifyVisible = () => !!notify && notify.offsetParent !== null;

    const sync = () => {
      const mainLabel = main.querySelector('.pdp-v2-atc__label');
      const mainPrice = main.querySelector('[data-pdp-v2-sync]');
      const soldOut = main.hasAttribute('disabled');
      if (label) {
        if (soldOut && notifyVisible()) label.textContent = notify.textContent.trim();
        else if (mainLabel) label.textContent = mainLabel.textContent.trim();
      }
      if (mainPrice && price) price.textContent = mainPrice.textContent.trim();
      bar.classList.toggle('is-sold-out', soldOut);
    };

    new IntersectionObserver(([entry]) => {
      pastButton = !entry.isIntersecting && entry.boundingClientRect.top < 0;
      render();
    }).observe(anchor);

    if (footer) {
      new IntersectionObserver(([entry]) => {
        footerInView = entry.isIntersecting;
        render();
      }).observe(footer);
    }

    new MutationObserver(sync).observe(main, {
      attributes: true,
      attributeFilter: ['disabled'],
      childList: true,
      subtree: true,
      characterData: true
    });

    mobile.addEventListener('change', render);

    if (notify) {
      new MutationObserver(sync).observe(notify, { attributes: true, attributeFilter: ['style', 'class'] });
    }

    button.addEventListener('click', () => {
      if (main.hasAttribute('disabled') && notifyVisible()) {
        notify.click();
        return;
      }
      if (main.hasAttribute('disabled')) {
        const picker = root.querySelector('.pdp-v2__block--variant_picker') || anchor;
        picker.scrollIntoView({ behavior: reduceMotion.matches ? 'auto' : 'smooth', block: 'center' });
        return;
      }
      main.click();
    });

    sync();
    render();
  };

  /* 4. Set card: colour dots swap the piece shown in a row --------------- */
  document.addEventListener('click', (event) => {
    const swatch = event.target.closest('.pdp-v2-set-row__swatch');
    if (!swatch) return;
    const row = swatch.closest('[data-pdp-v2-set-row]');
    if (!row) return;
    const data = swatch.dataset;

    row.querySelectorAll('.pdp-v2-set-row__swatch').forEach((el) => {
      const active = el === swatch;
      el.classList.toggle('is-active', active);
      el.setAttribute('aria-pressed', active ? 'true' : 'false');
    });

    row.querySelectorAll('[data-set-row-link]').forEach((el) => { el.href = data.url; });
    const img = row.querySelector('[data-set-row-img]');
    if (img && data.img) img.src = data.img;
    const colour = row.querySelector('[data-set-row-colour]');
    if (colour) colour.textContent = data.colour;
    const price = row.querySelector('[data-set-row-price]');
    if (price) price.textContent = data.price;
    const setPrice = row.querySelector('[data-set-row-set-price]');
    if (setPrice) setPrice.textContent = data.setPrice;
    const add = row.querySelector('[data-set-row-add]');
    if (add) {
      add.dataset.productUrl = data.quickUrl;
      const label = add.getAttribute('aria-label') || '';
      add.setAttribute('aria-label', `${label.split(':')[0]}: ${data.title}`);
    }
  });

  const initAll = () => {
    document.querySelectorAll('.pdp-v2').forEach(initStickyAtc);
  };

  const boot = () => {
    if (!initVariantSync()) {
      // pubsub/global load deferred as well; retry once they are there
      let tries = 0;
      const timer = window.setInterval(() => {
        tries += 1;
        if (initVariantSync() || tries > 40) window.clearInterval(timer);
      }, 100);
    }
    initAll();
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  document.addEventListener('shopify:section:load', initAll);
})();
